import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {IdleIterationSummary, iterateInIdleOrHiddenCallbackAsync} from '../../helpers/async/inIdleCallback';
import {AppState} from '../../store/appState';
import {buildCleanState} from './cleanHelpers';
import {
  GarbageCollectorFinishedState,
  GarbageCollectorStagesEnum,
  GarbageCollectorState,
  isFinishedState,
} from './gcTypes';
import {performInitialScan} from './initializationHelpers';
import {searchForReachableItems} from './searchHelpers';

/*
 * Service.
 */

/** Cleans the state in multiple idle callbacks. */
export async function cleanStateAsync(
  getState: () => AppState,
  shouldKeepCycles: boolean,
  onIterationSummary?: (summary: IdleIterationSummary) => void,
): Promise<AppState> {
  const initialGcState: GarbageCollectorState = {
    stage: GarbageCollectorStagesEnum.INITIALIZING,
  };

  const {newState} = await iterateInIdleOrHiddenCallbackAsync<
    GarbageCollectorState,
    GarbageCollectorFinishedState
  >(
    isFinishedState,
    (gcState) => {
      // Get the latest state since it may change between idle callbacks.
      const currentState = getState();
      return performGarbageCollectorCycle(gcState, currentState, shouldKeepCycles);
    },
    initialGcState,
    {
      source: TaskSourcesEnum.GARBAGE_COLLECTOR,
    },
    onIterationSummary,
  );

  return newState;
}

/*
 * Helpers.
 */

function performGarbageCollectorCycle(
  gcState: GarbageCollectorState,
  appState: AppState,
  shouldKeepCycles: boolean,
): GarbageCollectorState {
  const {stage} = gcState;
  if (stage === GarbageCollectorStagesEnum.INITIALIZING) {
    // Perform the initial scan in one garbage collector cycle.
    return performInitialScan(appState);
  }

  if (stage !== GarbageCollectorStagesEnum.SEARCHING) {
    // It is unsafe to enter a garbage collector cycle with a non-searching stage.
    // If we're in CLEANING, we can't guarantee that the state hasn't changed since, invaliding the search.
    // If we're in FINISHED, there is nothing to do.
    throw new ErrorWithData('Encountered invalid stage in garbage collector cycle ', {stage});
  }

  const newGcState = searchForReachableItems(gcState, appState, shouldKeepCycles);
  if (newGcState.stage === GarbageCollectorStagesEnum.SEARCHING) {
    // We're still searching. We'll resume in the next cycle.
    return newGcState;
  }

  // We need to perform the clean synchronously before the app state can change.
  return buildCleanState(newGcState, appState);
}
